import React, { useState, useMemo } from 'react'
import { IndicatorsColumn } from './IndicatorsColumn'
import { createIndicatorCard } from './indicators.data'
import './Indicators.scss'
import { useThrottle } from '@umijs/hooks'
import moment from 'moment'
import { liveSupport } from '../../../clients/iron-fit'
import { formatMinutes } from '@irontrainers/live-assist/utils'
import { size } from 'lodash'

export const Indicators: React.FC = () => {
  const oneHourAgo = useThrottle(moment.utc().subtract(1, 'hours').valueOf(), 5 * 60 * 1000)
  const [lastFailedSupports] = liveSupport.useLiveSupports({ status: 'FAILED', startTime: oneHourAgo })
  const [lastFinishedSupports] = liveSupport.useLiveSupports({ status: 'FINISHED', startTime: oneHourAgo })

  const [psychologistsAwaitingPatient, psychologistsAwaitingPatientLoading] = liveSupport.useLivePsychologistsAwaitingPatient('video')
  const [psychologistsInSupport, psychologistsInSupportLoading] = liveSupport.useLivePsychologistsInSupport(true)

  const [assistantsAwaitingPatient, assistantsAwaitingPatientLoading] = liveSupport.useLiveAssistantsAwaitingPatient('video')
  const [assistantsInSupport, assistantsInSupportLoading] = liveSupport.useLiveAssistantsInSupport(true)

  const [doctorsAwaitingPatient, doctorsAwaitingPatientLoading] = liveSupport.useLiveDoctorsAwaitingPatient('video')
  const [doctorsInSupport, doctorsInSupportLoading] = liveSupport.useLiveDoctorsInSupport(true)

  const [operatorsAwaitingPatient, operatorsAwaitingPatientLoading] = liveSupport.useLiveOperatorsAwaitingPatient('video')
  const [operatorsInSupport, operatorsInSupportLoading] = liveSupport.useLiveOperatorsInSupport(true)

  const { psychologistsAwaitingPatientCount, psychologistsInSupportCount } = React.useMemo(() => {
    if (psychologistsAwaitingPatientLoading || psychologistsInSupportLoading) {
      return { psychologistsAwaitingPatientCount: 0, psychologistsInSupportCount: 0 }
    }
    return {
      psychologistsAwaitingPatientCount: size(psychologistsAwaitingPatient),
      psychologistsInSupportCount: size(psychologistsInSupport),
    }
  }, [psychologistsAwaitingPatientLoading, psychologistsInSupportLoading, psychologistsAwaitingPatient, psychologistsInSupport])

  const { operatorsAwaitingPatientCount, operatorsInSupportCount } = React.useMemo(() => {
    if (operatorsAwaitingPatientLoading || operatorsInSupportLoading) {
      return { operatorsAwaitingPatientCount: 0, operatorsInSupportCount: 0 }
    }
    return {
      operatorsAwaitingPatientCount: size(operatorsAwaitingPatient),
      operatorsInSupportCount: size(operatorsInSupport),
    }
  }, [operatorsAwaitingPatientLoading, operatorsInSupportLoading, operatorsAwaitingPatient, operatorsInSupport])

  const { assistantsAwaitingPatientCount, assistantsInSupportCount } = React.useMemo(() => {
    if (assistantsAwaitingPatientLoading || assistantsInSupportLoading) {
      return { assistantsAwaitingPatientCount: 0, assistantsInSupportCount: 0 }
    }
    return {
      assistantsAwaitingPatientCount: size(assistantsAwaitingPatient),
      assistantsInSupportCount: size(assistantsInSupport),
    }
  }, [assistantsAwaitingPatientLoading, assistantsInSupportLoading, assistantsAwaitingPatient, assistantsInSupport])

  const { doctorsAwaitingPatientCount, doctorsInSupportCount,  pediatraAwaitingPatientCount, pediatraInSupportCount, clinicoAwaitingPatientCount, clinicoInSupportCount } = React.useMemo(() => {
    if (doctorsAwaitingPatientLoading || doctorsInSupportLoading) {
      return { doctorsAwaitingPatientCount: 0, doctorsInSupportCount: 0, pediatraAwaitingPatientCount: 0, pediatraInSupportCount: 0, clinicoAwaitingPatientCount: 0, clinicoInSupportCount: 0 }
    }

    let doctorsInSupportCount = 0;
    let pediatraInSupportCount = 0;
    for (let key in doctorsInSupport) {
      const department = doctorsInSupport[key].department;
      doctorsInSupportCount++;
      if (department === "Pediatrician") pediatraInSupportCount++;
    }
    const clinicoInSupportCount = doctorsInSupportCount - pediatraInSupportCount;

    let doctorsAwaitingPatientCount = 0;
    let pediatraAwaitingPatientCount = 0;

    for (let key in doctorsAwaitingPatient) {
      const department = doctorsAwaitingPatient[key].department;
      doctorsAwaitingPatientCount++;
      if (department === "Pediatrician") pediatraAwaitingPatientCount++;
    }
    const clinicoAwaitingPatientCount = doctorsAwaitingPatientCount - pediatraAwaitingPatientCount;

    return { doctorsAwaitingPatientCount, doctorsInSupportCount, pediatraAwaitingPatientCount, pediatraInSupportCount, clinicoAwaitingPatientCount, clinicoInSupportCount }
  }, [doctorsAwaitingPatientLoading, doctorsInSupportLoading, doctorsAwaitingPatient, doctorsInSupport])

  // average waiting time
  const {
    doctorsAvgPatientWaitingDuration,
    operatorsAvgPatientWaitingDuration,
    psychologistAvgPatientWaitingDuration,
    assistantAvgPatientWaitingDuration,
    pediatraAvgPatientWaitingDuration,
    clinicoAvgPatientWaitingDuration,
    patientsAttendedByOperator,
    patientsAttendedByDoctors,
    patientsAttendedByAssistant,
    patientsAttendedByPsychologist,
    patientsAttendedByPediatra,
    patientsAttendedByClinico,
    operatorsSuccessRate,
    doctorsSuccessRate,
    assistantsSuccessRate,
    psychologistSuccessRate,
    pediatraSuccessRate,
    clinicoSuccessRate,
  } = React.useMemo(() => {
    if (!lastFailedSupports && !lastFinishedSupports)
      return {
        doctorsAvgPatientWaitingDuration: formatMinutes(0),
        operatorsAvgPatientWaitingDuration: formatMinutes(0),
        psychologistAvgPatientWaitingDuration: formatMinutes(0),
        assistantAvgPatientWaitingDuration: formatMinutes(0),
        pediatraAvgPatientWaitingDuration: formatMinutes(0),
        clinicoAvgPatientWaitingDuration: formatMinutes(0),
        patientsAttendedByOperator: 0,
        patientsAttendedByAssistant: 0,
        patientsAttendedByDoctors: 0,
        patientsAttendedByPsychologist: 0,
        patientsAttendedByPediatra: 0,
        patientsAttendedByClinico: 0,
        operatorsSuccessRate: '-',
        doctorsSuccessRate: '-',
        assistantsSuccessRate: '-',
        psychologistSuccessRate: '-',
        pediatraSuccessRate: '-',
        clinicoSuccessRate: '-',
      }
    let doctorsSupportsCount = 0
    let doctorsSupportsWaitingDuration = 0

    let operatorsSupportsCount = 0
    let operatorsSupportsWaitingDuration = 0

    let assistantsSupportsCount = 0
    let assistantsSupportsWaitingDuration = 0

    let psychologistsSupportsCount = 0
    let psychologistsSupportsWaitingDuration = 0

    let doctorsSuccessSupportCount = 0
    let operatorsSuccessSupportCount = 0
    let assistantsdoctorSuccessSupportCount = 0
    let psychologistsSuccessSupportCount = 0

    let pediatraSupportCount = 0
    let pediatraSupportsWaitingDuration = 0
    let pediatraSuccessSupportCount = 0

    let clinicoSupportCount = 0
    let clinicoSupportsWaitingDuration = 0
    let clinicoSuccessSupportCount = 0

    for (const supportId in lastFailedSupports) {
      const support = lastFailedSupports[supportId]
      // if (support.corporationId !== CORPORATION_SLUG) continue;
      if (support.staffType === 'doctor') {
        doctorsSupportsCount += 1
        doctorsSupportsWaitingDuration += support.patientWaitingTime
        if (support.patientTransferredWithPreferredStaffDepartment === 'Pediatrician') {
          pediatraSupportCount += 1
          pediatraSupportsWaitingDuration += support.patientWaitingTime
        } else {
          clinicoSupportCount += 1
          clinicoSupportsWaitingDuration += support.patientWaitingTime
        }
      }
      if (support.staffType === 'operator') {
        operatorsSupportsCount += 1
        operatorsSupportsWaitingDuration += support.patientWaitingTime
      }
      if (support.staffType === 'assistant') {
        assistantsSupportsCount += 1
        assistantsSupportsWaitingDuration += support.patientWaitingTime
      }
      if (support.staffType === 'psychologist') {
        psychologistsSupportsCount += 1
        psychologistsSupportsWaitingDuration += support.patientWaitingTime
      }
    }
    for (const supportId in lastFinishedSupports) {
      const support = lastFinishedSupports[supportId]
      if (support.staffType === 'doctor') {
        doctorsSupportsCount += 1
        doctorsSuccessSupportCount += 1
        doctorsSupportsWaitingDuration += support.patientWaitingTime
        if (support.patientTransferredWithPreferredStaffDepartment === 'Pediatrician') {
          pediatraSupportCount += 1
          pediatraSupportsWaitingDuration += support.patientWaitingTime
          pediatraSuccessSupportCount += 1
        } else {
          clinicoSupportCount += 1
          clinicoSupportsWaitingDuration += support.patientWaitingTime
          clinicoSuccessSupportCount += 1
        }
      }
      if (support.staffType === 'operator') {
        operatorsSupportsCount += 1
        operatorsSuccessSupportCount += 1
        operatorsSupportsWaitingDuration += support.patientWaitingTime
      }
      if (support.staffType === 'assistant') {
        assistantsSupportsCount += 1
        assistantsdoctorSuccessSupportCount += 1
        assistantsSupportsWaitingDuration += support.patientWaitingTime
      }
      if (support.staffType === 'psychologist') {
        psychologistsSupportsCount += 1
        psychologistsSuccessSupportCount += 1
        psychologistsSupportsWaitingDuration += support.patientWaitingTime
      }
    }
    const doctorsAverage = doctorsSupportsCount > 0 ? doctorsSupportsWaitingDuration / doctorsSupportsCount : 0
    const operatorsAverage = operatorsSupportsCount > 0 ? operatorsSupportsWaitingDuration / operatorsSupportsCount : 0
    const assistantsAverage = assistantsSupportsCount > 0 ? assistantsSupportsWaitingDuration / assistantsSupportsCount : 0
    const psychologistsAverage = psychologistsSupportsCount > 0 ? psychologistsSupportsWaitingDuration / psychologistsSupportsCount : 0
    const pediatraAverage = pediatraSuccessSupportCount > 0 ? pediatraSupportsWaitingDuration / pediatraSuccessSupportCount : 0
    const clinicoAverage = clinicoSuccessSupportCount > 0 ? clinicoSupportsWaitingDuration / clinicoSuccessSupportCount : 0

    const calculateSuccessRate = (successCount: number, totalCount: number) => 
      totalCount > 0 && successCount > 0 ? `${(parseFloat((successCount / totalCount).toFixed(1)) * 100)}%` : '-';

    return {
      doctorsAvgPatientWaitingDuration: formatMinutes(doctorsAverage),
      operatorsAvgPatientWaitingDuration: formatMinutes(operatorsAverage),
      assistantAvgPatientWaitingDuration: formatMinutes(assistantsAverage),
      psychologistAvgPatientWaitingDuration: formatMinutes(psychologistsAverage),
      pediatraAvgPatientWaitingDuration: formatMinutes(pediatraAverage),
      clinicoAvgPatientWaitingDuration: formatMinutes(clinicoAverage),
      patientsAttendedByOperator: operatorsSupportsCount,
      patientsAttendedByDoctors: doctorsSupportsCount,
      patientsAttendedByAssistant: assistantsSupportsCount,
      patientsAttendedByPsychologist: psychologistsSupportsCount,
      patientsAttendedByPediatra: pediatraSupportCount,
      patientsAttendedByClinico: clinicoSupportCount,
      operatorsSuccessRate: calculateSuccessRate(operatorsSuccessSupportCount, operatorsSupportsCount),
      doctorsSuccessRate: calculateSuccessRate(doctorsSuccessSupportCount, doctorsSupportsCount),
      assistantsSuccessRate: calculateSuccessRate(assistantsdoctorSuccessSupportCount, assistantsSupportsCount),
      psychologistSuccessRate: calculateSuccessRate(psychologistsSuccessSupportCount, psychologistsSupportsCount),
      pediatraSuccessRate: calculateSuccessRate(pediatraSuccessSupportCount, pediatraSupportCount),
      clinicoSuccessRate: calculateSuccessRate(clinicoSuccessSupportCount, clinicoSupportCount),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFailedSupports, lastFinishedSupports])

  // average support time
  const { doctorsAvgSupportDuration, operatorsAvgSupportDuration, psychologistsAvgSupportDuration, assistantsAvgSupportDuration, pediatraAvgSupportDuration, clinicoAvgSupportDuration } = React.useMemo(() => {
    if (!lastFinishedSupports)
      return {
        doctorsAvgSupportDuration: formatMinutes(0),
        operatorsAvgSupportDuration: formatMinutes(0),
        psychologistsAvgSupportDuration: formatMinutes(0),
        assistantsAvgSupportDuration: formatMinutes(0),
        pediatraAvgSupportDuration: formatMinutes(0),
        clinicoAvgSupportDuration: formatMinutes(0),
      }
    let doctorsSupportsCount = 0
    let doctorsSupportsTotalDuration = 0
    let operatorsSupportsCount = 0
    let operatorsSupportsTotalDuration = 0

    let assistantsSupportsCount = 0
    let assistantsSupportsTotalDuration = 0

    let psychologistsSupportsCount = 0
    let psychologistsSupportsTotalDuration = 0

    let pediatraSupportsCount = 0
    let pediatraSupportsTotalDuration = 0

    let clinicoSupportsCount = 0
    let clinicoSupportsTotalDuration = 0

    for (const supportId in lastFinishedSupports) {
      const support = lastFinishedSupports[supportId]
      if (!support.endTime) continue
      const duration = support.endTime - support.startTime
      if (duration <= 60 * 1000) continue
      if (support.staffType === 'doctor') {
        doctorsSupportsCount += 1
        doctorsSupportsTotalDuration += duration
        if (support.patientTransferredWithPreferredStaffDepartment === 'Pediatrician') {
          pediatraSupportsCount += 1
          pediatraSupportsTotalDuration += duration
        } else {
          clinicoSupportsCount += 1
          clinicoSupportsTotalDuration += duration
        }
      }
      if (support.staffType === 'operator') {
        operatorsSupportsCount += 1
        operatorsSupportsTotalDuration += duration
      }
      if (support.staffType === 'assistant') {
        assistantsSupportsCount += 1
        assistantsSupportsTotalDuration += duration
      }
      if (support.staffType === 'psychologist') {
        psychologistsSupportsCount += 1
        psychologistsSupportsTotalDuration += duration
      }
    }
    const doctorsAverage = doctorsSupportsCount > 0 ? doctorsSupportsTotalDuration / doctorsSupportsCount : 0
    const operatorsAverage = operatorsSupportsCount > 0 ? operatorsSupportsTotalDuration / operatorsSupportsCount : 0
    const assistantsAverage = assistantsSupportsCount > 0 ? assistantsSupportsTotalDuration / assistantsSupportsCount : 0
    const psychologistsAverage = psychologistsSupportsCount > 0 ? psychologistsSupportsTotalDuration / psychologistsSupportsCount : 0
    const pediatraAverage = pediatraSupportsCount > 0 ? pediatraSupportsTotalDuration / pediatraSupportsCount : 0
    const clinicoAverage = clinicoSupportsCount > 0 ? clinicoSupportsTotalDuration / clinicoSupportsCount : 0

    return {
      doctorsAvgSupportDuration: formatMinutes(doctorsAverage),
      operatorsAvgSupportDuration: formatMinutes(operatorsAverage),
      assistantsAvgSupportDuration: formatMinutes(assistantsAverage),
      psychologistsAvgSupportDuration: formatMinutes(psychologistsAverage),
      pediatraAvgSupportDuration: formatMinutes(pediatraAverage),
      clinicoAvgSupportDuration: formatMinutes(clinicoAverage),
    }
  }, [lastFinishedSupports])

  const [doctorFilter, setDoctorFilter] = useState<'all' | 'clinical' | 'pediatric'>('all')

  // Static data with dynamic filter handling for doctors
  const indicatorsStructure = useMemo(() => {
    const descriptionText = doctorFilter === 'clinical' ? 'clínicos' : doctorFilter === 'pediatric' ? 'pediatras' : 'profissionais'

    const doctorCardsByFilter = {
      onlineStaffs: doctorFilter === 'pediatric' ? (pediatraAwaitingPatientCount + pediatraInSupportCount) : doctorFilter === 'clinical' ? (clinicoAwaitingPatientCount + clinicoInSupportCount) : (doctorsAwaitingPatientCount + doctorsInSupportCount),
      doctorsInSupportCount: doctorFilter === 'pediatric' ? pediatraInSupportCount : doctorFilter === 'clinical' ? clinicoInSupportCount : doctorsInSupportCount,
      doctorsSuccessRate: doctorFilter === 'pediatric' ? pediatraSuccessRate : doctorFilter === 'clinical' ? clinicoSuccessRate : doctorsSuccessRate,
      patientsAttendedByDoctors: doctorFilter === 'pediatric' ? patientsAttendedByPediatra : doctorFilter === 'clinical' ? patientsAttendedByClinico : patientsAttendedByDoctors,
      doctorsAvgSupportDuration: doctorFilter === 'pediatric' ? pediatraAvgSupportDuration : doctorFilter === 'clinical' ? clinicoAvgSupportDuration : doctorsAvgSupportDuration,
      doctorsAvgPatientWaitingDuration: doctorFilter === 'pediatric' ? pediatraAvgPatientWaitingDuration : doctorFilter === 'clinical' ? clinicoAvgPatientWaitingDuration : doctorsAvgPatientWaitingDuration,
    }

    const baseData = {
      nursing: {
        title: 'INDICADORES DE ENFERMAGEM',
        cards: [
          createIndicatorCard('', 'green', 'On-line', operatorsAwaitingPatientCount + operatorsInSupportCount, 'profissionais'),
          createIndicatorCard('', 'blue', 'Em atendimento', operatorsInSupportCount, 'profissionais'),
          createIndicatorCard('fas fa-check', 'purple', 'Taxa de sucesso (últimos 60 minutos)', operatorsSuccessRate, 'atendimentos completos'),
          createIndicatorCard('fas fa-users', 'purple', 'Pacientes atendidos (últimos 60 minutos)', patientsAttendedByOperator, 'Pacientes'),
          createIndicatorCard('fas fa-video', 'green', 'Tempo médio de atendimento (últimos 60 minutos)', operatorsAvgSupportDuration, 'minutos'),
          createIndicatorCard('fas fa-clock', 'purple', 'Tempo médio de espera (últimos 60 minutos)', operatorsAvgPatientWaitingDuration, 'minutos'),
        ],
      },
      doctors: {
        title: 'INDICADORES DE MÉDICOS',
        cards: [
          createIndicatorCard('', 'green', 'On-line', doctorCardsByFilter.onlineStaffs, descriptionText),
          createIndicatorCard('', 'blue', 'Em atendimento', doctorCardsByFilter.doctorsInSupportCount, descriptionText),
          createIndicatorCard('fas fa-check', 'purple', 'Taxa de sucesso (últimos 60 minutos)', doctorCardsByFilter.doctorsSuccessRate, 'atendimentos completos'),
          createIndicatorCard('fas fa-users', 'purple', 'Pacientes atendidos (últimos 60 minutos)', doctorCardsByFilter.patientsAttendedByDoctors, 'Pacientes'),
          createIndicatorCard('fas fa-video', 'green', 'Tempo médio de atendimento (últimos 60 minutos)', doctorCardsByFilter.doctorsAvgSupportDuration, 'minutos'),
          createIndicatorCard('fas fa-clock', 'purple', 'Tempo médio de espera (últimos 60 minutos)', doctorCardsByFilter.doctorsAvgPatientWaitingDuration, 'minutos'),
        ],
        filters: {
          options: ['Todos', 'Clínicos', 'Pediatras'],
          active: 'Clínicos',
          onChange: () => {}, // Will be set by component
        },
      },
      psychologists: {
        title: 'INDICADORES DE PSICÓLOGOS',
        cards: [
          createIndicatorCard('', 'green', 'On-line', psychologistsInSupportCount + psychologistsAwaitingPatientCount, 'profissionais'),
          createIndicatorCard('', 'blue', 'Em atendimento', psychologistsInSupportCount, 'profissionais'),
          createIndicatorCard( 'fas fa-check', 'purple', 'Taxa de sucesso (últimos 60 minutos)', psychologistSuccessRate, 'atendimentos completos'),
          createIndicatorCard('fas fa-users', 'purple', 'Pacientes atendidos (últimos 60 minutos)', patientsAttendedByPsychologist, 'Pacientes'),
          createIndicatorCard('fas fa-video', 'green', 'Tempo médio de atendimento (últimos 60 minutos)', psychologistsAvgSupportDuration, 'minutos'),
          createIndicatorCard('fas fa-clock', 'purple', 'Tempo médio de espera (últimos 60 minutos)', psychologistAvgPatientWaitingDuration, 'minutos')
        ],
      },
      aps: {
        title: 'INDICADORES DE ACOLHIMENTO APS',
        cards: [
          createIndicatorCard('', 'green', 'On-line ', assistantsAwaitingPatientCount + assistantsInSupportCount, 'profissionais'),
          createIndicatorCard('', 'blue', 'Em atendimento', assistantsInSupportCount, 'profissionais'),
          createIndicatorCard('fas fa-check', 'purple', 'Taxa de sucesso (últimos 60 minutos)', assistantsSuccessRate, 'atendimentos completos'),
          createIndicatorCard('fas fa-users', 'purple', 'Pacientes atendidos (últimos 60 minutos)', patientsAttendedByAssistant, 'Pacientes'),
          createIndicatorCard('fas fa-video', 'green', 'Tempo médio de atendimento (últimos 60 minutos)', assistantsAvgSupportDuration, 'minutos'),
         createIndicatorCard('fas fa-clock', 'purple', 'Tempo médio de espera (últimos 60 minutos)', assistantAvgPatientWaitingDuration, 'minutos')
        ],
      },
    }

    return {
      ...baseData,
      doctors: {
        ...baseData.doctors,
        filters: {
          options: ['Todos', 'Clínicos', 'Pediatras'],
          active: doctorFilter === 'all' ? 'Todos' : doctorFilter === 'clinical' ? 'Clínicos' : 'Pediatras',
          onChange: (filter: string) => {
            if (filter === 'Todos') setDoctorFilter('all')
            else if (filter === 'Clínicos') setDoctorFilter('clinical')
            else if (filter === 'Pediatras') setDoctorFilter('pediatric')
          },
        },
      },
    }
  }, [lastFinishedSupports, lastFailedSupports, doctorFilter])

  return (
    <div className="indicators-container">
      <div className="indicators-container__columns">
        <IndicatorsColumn category={indicatorsStructure.nursing} />
        <IndicatorsColumn category={indicatorsStructure.doctors} />
        <IndicatorsColumn category={indicatorsStructure.psychologists} />
        <IndicatorsColumn category={indicatorsStructure.aps} />
      </div>
    </div>
  )
}
